<template>
  <div class="auth" role="presentation" @mousedown.self="$emit('close')">
    <section ref="dialog" class="auth__dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
      <button class="auth__close" type="button" aria-label="Закрыть окно входа" @click="$emit('close')">×</button>

      <template v-if="account">
        <img class="auth__logo" src="/assets/pomesto-logo.svg" alt="Поместо" />
        <h2 id="auth-title" class="auth__title">Профиль сохранён</h2>
        <p class="auth__lead">Теперь персонаж, монеты и покупки доступны после входа на другом устройстве.</p>
        <div class="auth__account">
          <img v-if="account.avatarUrl" :src="account.avatarUrl" alt="" referrerpolicy="no-referrer" />
          <span>{{ account.displayName || account.email || "VK ID" }}</span>
        </div>
        <button class="auth__secondary" type="button" :disabled="busy" @click="logout">Выйти из учётной записи</button>
      </template>

      <template v-else>
        <img class="auth__logo" src="/assets/pomesto-logo.svg" alt="Поместо" />
        <h2 id="auth-title" class="auth__title">Сохрани свой профиль</h2>
        <p class="auth__lead">Регистрация необязательна. Войди, чтобы не потерять персонажа, монеты и покупки.</p>

        <form v-if="step === 'email'" class="auth__form" @submit.prevent="requestCode">
          <label class="auth__label" for="auth-email">Email</label>
          <input
            id="auth-email"
            ref="emailInput"
            v-model.trim="email"
            class="auth__input"
            type="email"
            inputmode="email"
            autocomplete="email"
            maxlength="254"
            placeholder="name@example.com"
            required
          />
          <button class="auth__primary" type="submit" :disabled="busy || !emailValid">
            {{ busy ? "Отправляем…" : "Получить код" }}
          </button>
        </form>

        <form v-else class="auth__form" @submit.prevent="verifyCode">
          <p class="auth__sent">Код отправлен на <strong>{{ email }}</strong></p>
          <label class="auth__label" for="auth-code">Код из письма</label>
          <input
            id="auth-code"
            ref="codeInput"
            v-model="code"
            class="auth__input auth__input--code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            pattern="[0-9]{6}"
            placeholder="000000"
            required
            @input="code = code.replace(/\D/g, '').slice(0, 6)"
          />
          <button class="auth__primary" type="submit" :disabled="busy || code.length !== 6">
            {{ busy ? "Проверяем…" : "Войти и сохранить" }}
          </button>
          <button class="auth__back" type="button" :disabled="busy" @click="step = 'email'">Изменить email</button>
        </form>

        <p v-if="error" class="auth__error" role="alert">{{ error }}</p>

        <div class="auth__divider"><span>или</span></div>
        <div ref="vkContainer" class="auth__vk" aria-label="Вход через VK ID"></div>
        <p v-if="vkUnavailable" class="auth__vk-note">VK ID временно недоступен. Можно войти по email.</p>
        <p class="auth__privacy">Продолжая, ты соглашаешься на хранение данных профиля для работы учётной записи.</p>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import type { AuthAccount } from "../api/auth"
import { logoutRoute, requestEmailCodeRoute, verifyEmailCodeRoute, vkAuthRoute } from "../api/auth"
import { configureVkId, VKID } from "../shared/vkid"

const props = defineProps<{ sessionId: string; account?: AuthAccount | null }>()
const emit = defineEmits<{ close: []; authenticated: [account: AuthAccount, isNewAccount: boolean]; loggedOut: [] }>()

const dialog = ref<HTMLElement | null>(null)
const emailInput = ref<HTMLInputElement | null>(null)
const codeInput = ref<HTMLInputElement | null>(null)
const vkContainer = ref<HTMLElement | null>(null)
const email = ref("")
const code = ref("")
const step = ref<"email" | "code">("email")
const busy = ref(false)
const error = ref("")
const vkUnavailable = ref(false)
let oneTap: VKID.OneTap | null = null

const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value))

function messageOf(value: unknown, fallback: string) {
  return value instanceof Error && value.message ? value.message : fallback
}

async function requestCode() {
  if (!emailValid.value || busy.value) return
  busy.value = true
  error.value = ""
  try {
    await requestEmailCodeRoute.run(ctx, { email: email.value, deviceSessionId: props.sessionId })
    step.value = "code"
    await nextTick()
    codeInput.value?.focus()
  } catch (cause) {
    error.value = messageOf(cause, "Не удалось отправить код")
  } finally {
    busy.value = false
  }
}

async function verifyCode() {
  if (code.value.length !== 6 || busy.value) return
  busy.value = true
  error.value = ""
  try {
    const result = await verifyEmailCodeRoute.run(ctx, { email: email.value, code: code.value, deviceSessionId: props.sessionId })
    emit("authenticated", result.account, result.isNewAccount)
  } catch (cause) {
    error.value = messageOf(cause, "Не удалось подтвердить код")
  } finally {
    busy.value = false
  }
}

async function logout() {
  busy.value = true
  try {
    await logoutRoute.run(ctx)
    emit("loggedOut")
  } catch (cause) {
    error.value = messageOf(cause, "Не удалось выйти")
  } finally {
    busy.value = false
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") emit("close")
}

onMounted(async () => {
  document.addEventListener("keydown", onKeydown)
  emailInput.value?.focus()
  if (props.account || !vkContainer.value) return
  if (!configureVkId()) {
    vkUnavailable.value = true
    return
  }
  try {
    oneTap = new VKID.OneTap()
    oneTap
      .on(VKID.WidgetEvents.ERROR, () => { vkUnavailable.value = true })
      .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, async (payload: VKID.AuthResponse) => {
        busy.value = true
        error.value = ""
        try {
          const token = await VKID.Auth.exchangeCode(payload.code, payload.device_id)
          const result = await vkAuthRoute.run(ctx, { accessToken: token.access_token, deviceSessionId: props.sessionId })
          emit("authenticated", result.account, result.isNewAccount)
        } catch (cause) {
          error.value = messageOf(cause, "Не удалось войти через VK ID")
        } finally {
          busy.value = false
        }
      })
      .render({
        container: vkContainer.value,
        showAlternativeLogin: true,
        oauthList: [VKID.OAuthName.OK, VKID.OAuthName.MAIL],
        styles: { height: 48, borderRadius: 14 },
      })
  } catch {
    vkUnavailable.value = true
  }
})

watch(step, async (value) => {
  if (value === "email") {
    await nextTick()
    emailInput.value?.focus()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown)
  oneTap?.close()
})
</script>

<style scoped>
.auth { position: fixed; inset: 0; z-index: 12000; display: grid; place-items: center; padding: 18px; background: rgba(41,43,51,.48); backdrop-filter: blur(8px); }
.auth__dialog { position: relative; width: min(430px, 100%); max-height: calc(100dvh - 36px); overflow-y: auto; padding: 30px; border: 1px solid rgba(41,43,51,.09); border-radius: 26px; background: #FFFDF8; box-shadow: 0 28px 80px rgba(41,43,51,.24); color: #292B33; }
.auth__close { position: absolute; top: 12px; right: 12px; width: 42px; height: 42px; border: 0; border-radius: 50%; background: #F1EEE7; color: #292B33; font-size: 27px; line-height: 1; cursor: pointer; }
.auth__logo { display: block; width: 152px; height: auto; margin: 2px 0 20px; }
.auth__title { margin: 0; font-size: 28px; line-height: 1.1; letter-spacing: -.6px; }
.auth__lead { margin: 10px 0 22px; color: #666873; font-size: 14px; line-height: 1.5; }
.auth__form { display: grid; gap: 10px; }
.auth__label { font-size: 13px; font-weight: 700; }
.auth__input { width: 100%; min-height: 50px; padding: 12px 15px; border: 2px solid #E8E5F4; border-radius: 14px; background: #FBF9F4; color: #292B33; font: 600 16px/1.2 Inter, sans-serif; outline: 0; }
.auth__input:focus { border-color: #6258D9; background: white; }
.auth__input--code { text-align: center; font-size: 24px; letter-spacing: 7px; }
.auth__primary, .auth__secondary { min-height: 50px; border: 0; border-radius: 14px; background: #6258D9; color: white; font: 700 15px/1 Inter, sans-serif; cursor: pointer; }
.auth__primary:disabled, .auth__secondary:disabled { cursor: default; opacity: .55; }
.auth__back { justify-self: center; padding: 8px; border: 0; background: transparent; color: #6258D9; font-weight: 700; cursor: pointer; }
.auth__sent { margin: 0 0 4px; color: #666873; font-size: 13px; overflow-wrap: anywhere; }
.auth__error { margin: 12px 0 0; padding: 10px 12px; border-radius: 12px; background: #FFF0EC; color: #9B3526; font-size: 13px; font-weight: 600; }
.auth__divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; color: #8A8B93; font-size: 12px; }
.auth__divider::before, .auth__divider::after { content: ""; flex: 1; height: 1px; background: #E3E0D9; }
.auth__vk { min-height: 48px; }
.auth__vk-note, .auth__privacy { margin: 10px 0 0; color: #777983; font-size: 11px; line-height: 1.45; text-align: center; }
.auth__account { display: flex; align-items: center; gap: 12px; margin: 18px 0; padding: 12px; border-radius: 16px; background: #F5F2EC; font-weight: 700; overflow-wrap: anywhere; }
.auth__account img { width: 42px; height: 42px; border-radius: 50%; object-fit: cover; }
.auth__secondary { width: 100%; background: #292B33; }
@media (max-width: 480px) { .auth { padding: 10px; align-items: end; } .auth__dialog { width: 100%; max-height: calc(100dvh - 10px); padding: 24px 18px 20px; border-radius: 24px 24px 18px 18px; } .auth__title { font-size: 25px; } }
</style>
